import { useToast } from 'primevue/usetoast'
import type { RouteLocationRaw } from '#vue-router'
import type { ToastMessage } from '../types/Message'
import type { ToastMessageOptions } from 'primevue/toast'

export function useToastService() {
  const {
    $i18n: { t },
  } = useNuxtApp()
  const messages = useState<{
    [key: string]: ToastMessage
  }>('messages', () => ({}))

  const setToast = async (messageData: ToastMessage, life: number = 0) => {
    // Toast service se musí získat ještě před awaitem – po dynamickém importu
    // už nejsme v Nuxt kontextu (useNuxtApp by hodil chybu, typicky při SSR).
    const toast = usePrimeVueToast()
    const sha1 = await import('js-sha1')

    const hash = sha1.default(JSON.stringify(messageData))
    if (!messages.value[hash]) {
      messages.value[hash] = { ...messageData, count: 1, life }
      toast?.add(messageData)
    } else if (messages.value[hash].count) {
      messages.value[hash].count++
    }
  }

  const addSuccessMessage = (
    message: string,
    heading: string | null = null,
    life = 5000,
    link?: RouteLocationRaw,
    action?: { label: string; click: () => void },
    data?: object
  ) => {
    setToast(
      {
        severity: 'success',
        summary: heading ?? message,
        detail: heading ? message : null,
        link,
        action,
        data,
      },
      life
    )
  }
  const addWarningMessage = (
    message: string,
    heading: string = t('Varování'),
    life = 60000,
    link?: RouteLocationRaw,
    action?: { label: string; click: () => void },
    data?: object
  ) => {
    setToast(
      {
        severity: 'warn',
        summary: heading,
        detail: message,
        link,
        data,
        action,
      },
      life
    )
  }
  const addInfoMessage = (
    summary: string,
    detail: string | null = null,
    life = 60000,
    link?: RouteLocationRaw,
    action?: { label: string; click: () => void },
    data?: object
  ) => {
    setToast(
      {
        severity: 'info',
        summary,
        detail,
        link,
        action,
        data,
      },
      life
    )
  }
  const addErrorMessage = (
    message: string,
    heading: string = t('Chyba'),
    life = 0,
    link?: RouteLocationRaw,
    action?: { label: string; click: () => void },
    data?: object
  ) => {
    setToast(
      {
        severity: 'error',
        summary: heading,
        detail: message,
        link,
        action,
        data,
      },
      life
    )
  }

  const clear = () => {
    const toast = usePrimeVueToast()
    toast.removeAllGroups()
  }

  const remove = (message: ToastMessageOptions) => {
    const toast = usePrimeVueToast()
    toast.remove(message)
  }

  return {
    clear,
    remove,
    messages,
    addSuccessMessage,
    addWarningMessage,
    addInfoMessage,
    addErrorMessage,
  }
}
export const usePrimeVueToast = () => {
  // tryUseNuxtApp: mimo Nuxt kontext (např. volání po awaitu) vrátí null místo chyby.
  const nuxtApp = tryUseNuxtApp()
  if (!nuxtApp) return undefined
  const getToast: typeof useToast = () => nuxtApp.vueApp.config.globalProperties.$toast
  const toastService = getToast()
  return toastService
}
